import { useTranslation } from "react-i18next"

export interface OnboardingPasswordRulesProps {
  /** Minimum password length, BE-supplied (ships through
   *  `password_requirements.min_length`). Used as the `count`
   *  interpolation when resolving each rule's i18n string. */
  minLength: number
  /** i18n keys for individual rules. BE ships
   *  `password_requirements.rule_keys` (currently `["length"]` — see
   *  OKS-306 / `User::PASSWORD_RULE_KEYS`). FE resolves each via
   *  `t("signup.password_rules.rules.${key}", { count: minLength })`.
   *  Adding a new rule = new entry in `User::PASSWORD_RULE_KEYS` +
   *  matching i18n key under `signup.password_rules.rules.*` in both
   *  `onboarding.en.yml` and `onboarding.fr.yml`. */
  ruleKeys: string[]
  /**
   * `id` attribute, so consumers can reference the rules block via
   * `aria-describedby` on the password input. UX Spec §5: SR reads
   * requirements before the field gets focus.
   */
  id: string
  className?: string
}

/**
 * OnboardingPasswordRules — static password-policy hints rendered above
 * the password input on Step 1.
 *
 * Rules are i18n-key driven so adding/removing a policy requirement is
 * a single backend constant change (no FE deploy needed).
 *
 * Renders nothing if `ruleKeys` is empty — defensive for the edge case
 * where Backend hasn't shipped `password_requirements` yet.
 */
export function OnboardingPasswordRules({
  minLength,
  ruleKeys,
  id,
  className = "",
}: OnboardingPasswordRulesProps) {
  const { t } = useTranslation()
  if (ruleKeys.length === 0) return null
  return (
    <div
      id={id}
      data-testid="onboarding-account-password-rules"
      className={`text-muted-foreground rounded-md border p-3 text-xs ${className}`}
    >
      <p className="text-foreground mb-1.5 font-medium">
        {t("signup.password_rules.heading")}
      </p>
      <ul role="list" className="list-disc space-y-0.5 pl-4">
        {ruleKeys.map((key) => (
          <li
            key={key}
            data-testid={`onboarding-account-password-rules-${key}`}
          >
            {t(`signup.password_rules.rules.${key}`, { count: minLength })}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default OnboardingPasswordRules
